import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MagnifyingGlassIcon, FunnelIcon, RocketLaunchIcon, XIcon, SpinnerGapIcon, ArrowSquareOutIcon } from "@phosphor-icons/react";
import { fetchCoins } from "../lib/api";
import { useChain } from "../context/ChainContext";
import { fmtUsd, scoreColor } from "../lib/format";
import { verdictColor } from "./DegenScore";

const scoreFilters = [
  { label: "All", value: 0 },
  { label: "45+", value: 45 },
  { label: "55+", value: 55 },
  { label: "75+", value: 75 },
];

const isCA = (v) => /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(v.trim());

export const Filters = ({ search, setSearch, minScore, setMinScore, surging, setSurging }) => {
  const navigate = useNavigate();
  const { chain } = useChain();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [debounced, setDebounced] = useState("");
  const boxRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 200);
    return () => clearTimeout(t);
  }, [search]);

  const showSuggest = open && debounced.length >= 2 && !isCA(debounced);

  const suggestQ = useQuery({
    queryKey: ["searchSuggest", chain, debounced],
    queryFn: () => fetchCoins({ chain, search: debounced, sort: "score", order: "desc" }),
    enabled: showSuggest,
    staleTime: 30000,
  });

  const results = (suggestQ.data?.coins || []).slice(0, 6);

  useEffect(() => { setActive(-1); }, [debounced]);

  // Close the dropdown when clicking outside the search box.
  useEffect(() => {
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const openCoin = (addr) => {
    setOpen(false);
    navigate(`/coin/${addr}`);
  };

  const onKeyDown = (e) => {
    if (isCA(search.trim()) && e.key === "Enter") { openCoin(search.trim()); return; }
    if (!showSuggest || results.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); openCoin(results[active >= 0 ? active : 0].address); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div className="border border-white/10 bg-card p-4 flex flex-col lg:flex-row lg:items-center gap-4">
      <div className="relative flex-1 min-w-0" ref={boxRef}>
        <MagnifyingGlassIcon size={20} weight="bold" className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neon pointer-events-none z-10" />
        <input
          data-testid="search-input"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Paste a contract address, or search name / ticker…"
          autoComplete="off"
          spellCheck="false"
          className="w-full bg-black/50 border-2 border-neon/40 focus:border-neon pl-11 pr-10 py-3.5 text-base font-mono text-white placeholder:text-muted-foreground/70 focus:outline-none shadow-[0_0_18px_-6px_rgba(0,229,153,0.45)] focus:shadow-[0_0_24px_-4px_rgba(0,229,153,0.7)] transition-all"
        />
        {search && (
          <button
            data-testid="search-clear"
            onClick={() => { setSearch(""); setOpen(false); }}
            title="Clear search"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white transition-colors z-10"
          >
            <XIcon size={16} weight="bold" />
          </button>
        )}

        {showSuggest && (
          <div
            data-testid="search-suggestions"
            className="absolute left-0 right-0 top-full mt-1.5 z-50 bg-[#0A0A0A] border border-neon/30 shadow-[0_12px_40px_-8px_rgba(0,0,0,0.9)] max-h-[360px] overflow-y-auto animate-fade-up"
          >
            {suggestQ.isLoading ? (
              <div className="flex items-center gap-2 px-4 py-4 text-muted-foreground text-sm font-mono">
                <SpinnerGapIcon size={16} className="animate-spin text-neon" /> Searching…
              </div>
            ) : results.length === 0 ? (
              <div className="px-4 py-4 text-muted-foreground text-sm font-mono">
                No tokens found for "{debounced}". Paste the full contract address to open it directly.
              </div>
            ) : (
              <>
                {results.map((c, i) => (
                  <button
                    key={c.address}
                    data-testid={`search-suggestion-${c.symbol || "t"}-${c.address.slice(0, 6)}`}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => openCoin(c.address)}
                    className={`w-full flex items-center gap-3 px-3.5 py-2.5 text-left border-b border-white/5 last:border-b-0 transition-colors ${active === i ? "bg-neon/10" : "hover:bg-white/5"}`}
                  >
                    {c.image ? (
                      <img src={c.image} alt="" className="w-8 h-8 object-cover border border-white/10 shrink-0" onError={(e) => (e.target.style.display = "none")} />
                    ) : (
                      <div className="w-8 h-8 flex items-center justify-center bg-white/5 border border-white/10 font-mono text-[10px] shrink-0">{(c.symbol || "?").slice(0, 3)}</div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-heading font-bold text-sm truncate">{c.symbol || "?"}</span>
                        <span className="text-xs text-muted-foreground truncate">{c.name}</span>
                      </div>
                      <div className="text-[10px] font-mono text-muted-foreground/70">{fmtUsd(c.marketCap)} mcap · {fmtUsd(c.liquidity)} liq</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-mono font-bold text-sm" style={{ color: scoreColor(c.score?.total ?? 0) }}>{c.score?.total ?? "—"}</div>
                      <div className="text-[9px]" style={{ color: verdictColor(c.score?.verdict) }}>{c.score?.verdict}</div>
                    </div>
                    <ArrowSquareOutIcon size={14} className="text-muted-foreground shrink-0" />
                  </button>
                ))}
                <div className="px-3.5 py-2 text-[10px] font-mono text-muted-foreground/60 border-t border-white/5">
                  Tip: paste a full contract address to jump straight to any token.
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <button
        data-testid="filter-surging"
        onClick={() => setSurging(!surging)}
        className={`flex items-center gap-2 px-3 py-1.5 text-xs font-mono border transition-colors duration-150 whitespace-nowrap ${
          surging ? "bg-neon/10 border-neon/50 text-neon" : "border-white/10 text-muted-foreground hover:text-white"
        }`}
      >
        <RocketLaunchIcon size={14} weight={surging ? "fill" : "regular"} /> Surging
      </button>

      <div className="flex items-center gap-2">
        <span className="text-[11px] uppercase tracking-wider text-muted-foreground hidden sm:flex items-center gap-1">
          <FunnelIcon size={14} /> Score
        </span>
        {scoreFilters.map((f) => (
          <button
            key={f.label}
            data-testid={`filter-score-${f.value}`}
            onClick={() => setMinScore(f.value)}
            className={`px-3 py-1.5 text-xs font-mono border transition-colors duration-150 ${
              minScore === f.value ? "bg-neon/10 border-neon/40 text-neon" : "border-white/10 text-muted-foreground hover:text-white"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>
    </div>
  );
};
